import { useRef } from "react";
import { getJobFieldName, getAcademicFieldName } from "./fieldMapper";

/**
 * Shared utilities for alumni card functionality
 * Used by both search page and about page
 */

/**
 * Update the adaptive strip gradient and shadow based on mouse position
 * @param {HTMLElement} card - Card DOM element
 * @param {number} x - Mouse X position relative to card
 * @param {number} y - Mouse Y position relative to card
 * @param {number} cardWidth - Card width
 * @param {number} cardHeight - Card height
 */
const updateAdaptiveGradient = (card, x, y, cardWidth, cardHeight) => {
  // Calculate percentage position for horizontal gradient center
  const xPercent = (x / cardWidth) * 100;
  
  // Get theme-aware colors from CSS custom properties
  const computedStyles = getComputedStyle(document.documentElement);
  const color1 = computedStyles.getPropertyValue('--adaptive-color-1').trim() || '#ff6b6b';
  const color2 = computedStyles.getPropertyValue('--adaptive-color-2').trim() || '#ee5a24';
  const color3 = computedStyles.getPropertyValue('--adaptive-color-3').trim() || '#ff9ff3';
  
  // Create a linear gradient that shifts the color concentration based on mouse X position
  // The gradient will have more intense colors near the mouse position
  const leftColorStop = Math.max(0, xPercent - 20);
  const centerColorStop = xPercent;
  const rightColorStop = Math.min(100, xPercent + 20);
  
  // Create adaptive linear gradient with the color intensity following the mouse
  const gradient = `linear-gradient(90deg, 
    ${color1} 0%, 
    ${color1} ${leftColorStop}%, 
    ${color2} ${centerColorStop}%, 
    ${color3} ${rightColorStop}%, 
    ${color3} 100%)`;
  
  // Calculate adaptive shadow based on mouse position
  const centerX = cardWidth / 2;
  const centerY = cardHeight / 2;
  
  // Calculate shadow offset (shadow cast opposite to light source at mouse position)
  const shadowX = (centerX - x) / 8; // More subtle offset
  const shadowY = (centerY - y) / 8; // More subtle offset
  
  // Create gradient-colored shadow for depth using theme colors
  // Convert hex colors to rgba for shadow opacity
  const getRgbaFromHex = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  // Use gradient colors for sophisticated shadow effect
  const shadowColor1 = getRgbaFromHex(color1, 0.12); // Primary color shadow
  const shadowColor2 = getRgbaFromHex(color2, 0.08); // Secondary color shadow
  const shadowColor3 = getRgbaFromHex(color3, 0.06); // Accent color shadow
  
  const depthShadow = `${shadowX * 0.5}px ${shadowY * 0.5}px 8px ${shadowColor1}`;
  const baseShadow = `${shadowX}px ${shadowY}px 20px ${shadowColor2}`;
  const accentShadow = `${shadowX * 1.5}px ${shadowY * 1.5}px 40px ${shadowColor3}`;
  const adaptiveShadow = `${depthShadow}, ${baseShadow}, ${accentShadow}`;
  
  // Apply the gradient and shadow to custom CSS properties
  card.style.setProperty('--adaptive-strip-gradient', gradient);
  card.style.setProperty('--adaptive-shadow', adaptiveShadow);
  card.classList.add('adaptive-gradient-active');
  
  // Add floating effect
  card.style.transform = 'translateY(-8px) scale(1.02)';
};

/**
 * Hook for managing alumni card interactions
 * @returns {Object} Object containing card refs and interaction handlers
 */
export const useAlumniCards = () => {
  const cardRefs = useRef({});

  /**
   * Mouse tracking for tilt effect and adaptive gradient
   * @param {MouseEvent} e - Mouse event
   * @param {string} cardId - Unique identifier for the card
   */
  const handleMouseMove = (e, cardId) => {
    const cardRef = cardRefs.current[cardId];
    if (!cardRef) return;
    
    const card = cardRef;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Rotation logic - DISABLED for now, uncomment to re-enable
    /*
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    
    // Apply tilt transform
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.008)`;
    card.classList.add('tilted');
    */
    
    // Update adaptive gradient position
    updateAdaptiveGradient(card, x, y, rect.width, rect.height);
  };

  /**
   * Reset card transform and gradient when mouse leaves
   * @param {string} cardId - Unique identifier for the card
   */
  const handleMouseLeave = (cardId) => {
    const cardRef = cardRefs.current[cardId];
    if (!cardRef) return;
    
    const card = cardRef;
    
    // Reset floating effect immediately
    card.style.transform = 'translateY(0px) scale(1)';
    
    // Rotation reset logic - DISABLED for now, uncomment to re-enable
    /*
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
    card.classList.remove('tilted');
    */
    
    // Reset adaptive gradient and shadow with slight delay to allow smooth transition
    setTimeout(() => {
      if (cardRefs.current[cardId]) { // Check if card still exists
        card.style.removeProperty('--adaptive-strip-gradient');
        card.style.removeProperty('--adaptive-shadow');
        card.classList.remove('adaptive-gradient-active');
      }
    }, 50); // Small delay to prevent flashing
  };

  /**
   * Set card reference
   * @param {string} cardId - Unique identifier for the card
   * @param {HTMLElement} element - Card DOM element
   */
  const setCardRef = (cardId, element) => {
    cardRefs.current[cardId] = element;
  };

  return {
    cardRefs,
    handleMouseMove,
    handleMouseLeave,
    setCardRef
  };
};

/**
 * Format school information for display
 * @param {number|string} startTime - Start year
 * @param {number|string} className - Class number
 * @param {string} schoolType - Type of school (e.g., '初中', '高中')
 * @returns {string|null} Formatted school information or null if invalid
 */
export const formatSchoolInfo = (startTime, className, schoolType) => {
  if (!startTime && !className) return null;
  if (!startTime || startTime === 'null') return null;
  if (!className || className === 'null') return null;
  
  const yearText = startTime && startTime !== 'null' ? `${startTime}级` : '';
  const classText = className && className !== 'null' ? `${className}班` : '';
  
  if (!yearText && !classText) return null;
  
  return `${schoolType}${yearText}${classText}`;
};

/**
 * Generate card ID for consistent referencing
 * @param {string} prefix - Prefix for the card ID
 * @param {number} index - Index of the card
 * @returns {string} Unique card ID
 */
export const generateCardId = (prefix, index) => `${prefix}-${index}`;

/**
 * Get job field display name
 * @param {number|string} jobField - Job field value
 * @returns {string} Display name for the job field
 */
export const getJobFieldDisplayName = (jobField) => {
  return getJobFieldName(jobField);
};

/**
 * Format career information for display
 * @param {Object} member - Member object containing career data
 * @returns {string} Formatted career information
 */
export const formatCareerInfo = (member) => {
  const { employer, job_desc, job_field } = member;
  
  if (!employer && !job_desc && !job_field) {
    return '暂无信息';
  }
  
  const parts = [
    employer,
    job_field ? getJobFieldDisplayName(job_field) : null,
    job_desc
  ].filter(Boolean);
  
  return parts.join(' • ');
};

/**
 * Format education information for display
 * Shows the last (most recent) education experience
 * @param {Object} member - Member object containing education data
 * @returns {string} Formatted education information
 */
export const formatEducationInfo = (member) => {
  // Get all education entries
  const educationEntries = [
    { school: member.school1, major: member.school1_major, level: member.school1_level, startTime: member.school1_start_time, endTime: member.school1_end_time },
    { school: member.school2, major: member.school2_major, level: member.school2_level, startTime: member.school2_start_time, endTime: member.school2_end_time },
    { school: member.school3, major: member.school3_major, level: member.school3_level, startTime: member.school3_start_time, endTime: member.school3_end_time },
    { school: member.school4, major: member.school4_major, level: member.school4_level, startTime: member.school4_start_time, endTime: member.school4_end_time }
  ];
  
  // Filter out empty entries and find the last valid one
  const validEntries = educationEntries.filter(entry => 
    entry.school && entry.school !== null && entry.school !== undefined && entry.school !== '' && entry.school !== 'null'
  );
  
  if (validEntries.length === 0) {
    return '暂无信息';
  }
  
  // Get the last (most recent) education entry
  const lastEntry = validEntries[validEntries.length - 1];
  
  const parts = [
    lastEntry.school,
    lastEntry.major,
    lastEntry.level ? getAcademicFieldName(lastEntry.level) : null
  ].filter(Boolean);
  
  return parts.join(' • ');
};

/**
 * Check if career information is empty
 * @param {Object} member - Member object
 * @returns {boolean} True if career info is empty
 */
export const isCareerInfoEmpty = (member) => {
  return !member.employer && !member.job_desc && !member.job_field;
};

/**
 * Check if education information is empty
 * @param {Object} member - Member object
 * @returns {boolean} True if education info is empty
 */
export const isEducationInfoEmpty = (member) => {
  // Check all school fields
  return !member.school1 && !member.school2 && !member.school3 && !member.school4;
};
